import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faAdd,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons'
import { getComplaints } from 'client/utils/api-routes-orders'

import CalendarIcon from 'client/utils/icons/calendar-icon'
import ProductCatalogIcon from 'client/utils/icons/product-catalog-icon'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  $DCprimaryColor,
  DcButton,
  Link,
  PixelCollapse,
  PixelDate,
  PixelDiv,
  PixelFactoryContext,
  PixelFlexBox,
  PixelHeaderPanel,
  PixelIcon,
  PixelLeftPanel,
  PixelText,
  SectionContent,
  DcFlexBox,
  DcOverlaySlider,
  DcTag,
  colors,
} from 'pixel-base'
import styled from 'styled-components'

import AddComplaint from './subComponents/AddComplaint'
import { IPixelFactory, OrderDetail } from '../../../../../decl.d'

interface IComplaint {
  complaint_id: string
  complaint_status: string
  complaint_type: string
  created_at: string
}

interface IProps {
  order: OrderDetail
  detailID: string
  setDetails: (details: OrderDetail) => void
}
const Complaint = (props: IProps) => {
  const { detailID, setDetails, order } = props
  const { state, complaintId, setComplaintId } = React.useContext(OrdersContext)
  const [showShow, setShowShow] = React.useState(true)
  const navigate = useNavigate()

  const { showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)

  const complaintRef = React.useRef(null)
  const [showAddComplaintPopup, setShowAddComplaintPopup] =
    React.useState(false)
  const [complaints, setComplaints] = React.useState<IComplaint[]>([])

  const fetchComplaints = () => {
    showPageLoader?.()
    getComplaints(detailID).subscribe({
      next: (res: any) => {
        hidePageLoader?.()
        setComplaints(res?.response?.data || [])
      },
      error: (error) => {
        hidePageLoader?.()
        console.error('Error fetching complaints:', error)
      },
    })
  }

  React.useEffect(() => {
    if (detailID) {
      fetchComplaints()
    }
    return () => {
      hidePageLoader?.()
    }
  }, [detailID])

  React.useEffect(() => {
    if (complaintId) {
      navigate('/orders/support')
    }
  }, [complaintId])

  const isComplaint = complaints.length > 0
  return (
    <Container>
      <>
        <PixelFlexBox
          justifyContent="flex-start"
          alignItems="flex-start"
          borderBottom="1px solid #e6e6e6"
          width="100%"
          flexDirection="column"
        >
          <PixelCollapse
            onCollapseChange={(collapse) => {
              setShowShow(collapse)
            }}
            collapseController={
              <PixelHeaderPanel>
                <PixelLeftPanel>
                  <PixelText
                    textSize="16px"
                    style={{ fontWeight: '600', padding: '10px' }}
                    customColor="rgba(0, 0, 0, 0.84)"
                  >
                    {complaints.length === 0 ? (
                      <>
                        {order?.complaint_count > 0 ? (
                          <>Complaint ({order?.complaint_count})</>
                        ) : (
                          'Complaint'
                        )}
                      </>
                    ) : (
                      <>
                        {complaints.length > 0 ? (
                          <> Complaint ({complaints.length})</>
                        ) : (
                          'Complaint'
                        )}
                      </>
                    )}
                  </PixelText>
                  <HeaderEnd>
                    <DcButton
                      size="sm"
                      variant="link"
                      onClick={() => setShowAddComplaintPopup(true)}
                    >
                      <PixelIcon
                        color={$DCprimaryColor}
                        icon={faAdd}
                        tooltip="Add Complaint"
                        showTooltip
                      />
                    </DcButton>
                  </HeaderEnd>
                </PixelLeftPanel>
                {isComplaint && (
                  <PixelIcon
                    style={{ cursor: 'pointer' }}
                    padding="10px"
                    id="collapse-controller"
                    icon={showShow ? faChevronUp : faChevronDown}
                  />
                )}
              </PixelHeaderPanel>
            }
          >
            <PixelDiv width="100%">
              {complaints.map((complaint, index) => (
                <PixelFlexBox
                  style={{
                    borderBottom: ' 1px solid #e6e6e6',
                    padding: '10px',
                  }}
                  key={`complaint-${complaint.complaint_id}-${index}`}
                >
                  <PixelFlexBox width="95%">
                    <PixelFlexBox alignItems="center" gap="20px">
                      <DcFlexBox
                        height="auto"
                        alignItems="center"
                        gap="5px"
                        width="fit-content"
                      >
                        <span
                          style={{
                            color: colors.factoryGray,
                            fontSize: '20px',
                          }}
                        >
                          #
                        </span>
                        <span>{complaint.complaint_id}</span>
                      </DcFlexBox>
                      <DcFlexBox
                        height="auto"
                        alignItems="center"
                        gap="5px"
                        width="fit-content"
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            color: colors.factoryGray,
                          }}
                        >
                          Status
                        </span>
                        <DcTag
                          variant="small"
                          textColor={colors.warning}
                          customBackgroundColor={colors.warningLight}
                        >
                          {complaint.complaint_status}
                        </DcTag>
                      </DcFlexBox>
                      <DcFlexBox
                        height="auto"
                        alignItems="center"
                        gap="5px"
                        width="fit-content"
                      >
                        <ProductCatalogIcon
                          color={colors.factoryGray}
                          size={16}
                        />

                        <DcTag
                          variant="small"
                          textColor={colors.info}
                          customBackgroundColor={colors.infoLight}
                        >
                          {complaint.complaint_type}
                        </DcTag>
                      </DcFlexBox>
                      <DcFlexBox
                        height="16px"
                        alignItems="flex-end"
                        gap="5px"
                        width="fit-content"
                      >
                        <CalendarIcon size={16} color={colors.factoryGray} />
                        <PixelDate
                          size="12px"
                          value={complaint.created_at}
                          format={'pixelStandard'}
                        />
                      </DcFlexBox>
                    </PixelFlexBox>
                  </PixelFlexBox>
                  <PixelFlexBox
                    justifyContent="baseline"
                    alignItems="baseline"
                    width="15%"
                    padding="3px 0px 0px 0px"
                  >
                    <Link
                      color={$DCprimaryColor}
                      onClick={() => {
                        navigate(`support?page=0&id=${complaint.complaint_id}`)
                      }}
                    >
                      Details
                    </Link>
                  </PixelFlexBox>
                </PixelFlexBox>
              ))}
            </PixelDiv>
          </PixelCollapse>
          <PixelFlexBox
            style={{ flexDirection: 'column', gap: '10px' }}
            ref={complaintRef}
          >
            {complaints.length > 0 ? (
              <div></div>
            ) : (
              <>
                {order?.complaint_count > 0 ? (
                  ''
                ) : (
                  <PixelText
                    textSize="16px"
                    style={{ fontWeight: '600', padding: '10px' }}
                    customColor="rgba(0, 0, 0, 0.84)"
                  >
                    <SectionBlock color="primary">No Complaints</SectionBlock>
                  </PixelText>
                )}
              </>
            )}
          </PixelFlexBox>
        </PixelFlexBox>
      </>
      <DcOverlaySlider
        width={'55%'}
        title={'Add Complaint'}
        isOpen={showAddComplaintPopup}
        onClose={() => setShowAddComplaintPopup(false)}
      >
        {showAddComplaintPopup && (
          <SectionContent>
            <AddComplaint
              setShowAddComplaintPopup={setShowAddComplaintPopup}
              state={state}
              order={order}
              detailID={detailID}
              setDetails={setDetails}
              handleClose={() => {
                fetchComplaints()
                setShowAddComplaintPopup(false)
              }}
            />
          </SectionContent>
        )}
      </DcOverlaySlider>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SectionBlock = styled.div<{ color?: string }>`
  display: flex;
  font-size: 12px;
  font-weight: 400;
  margin-left: 10px;
  color: rgba(0, 0, 0, 0.87);
`

const HeaderEnd = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-basis: 100%;
  flex: 1;
  justify-content: flex-end;
  gap: 15px;
  padding: 10px 8px 10px 10px;
  cursor: pointer;
`
export default Complaint
